import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Comment } from '../types/comment';
import { loadProfileComments, updateCommentReadStatus } from '../services/commentService';

export function useInboxComments(selectedProfiles: string[]) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const loadComments = async () => {
    if (selectedProfiles.length === 0) {
      setComments([]);
      setSelectedCommentId(null);
      return;
    }

    setIsLoading(true);
    console.log('Chargement des commentaires pour les profils:', selectedProfiles);
    
    try {
      const sortedComments = await loadProfileComments(selectedProfiles);
      
      setComments(sortedComments);
      
      if (sortedComments.length === 0) {
        setSelectedCommentId(null);
      } else {
        if (!selectedCommentId || !sortedComments.some(c => c.id === selectedCommentId)) {
          const firstUnreadComment = sortedComments.find(c => !c.isRead);
          if (firstUnreadComment) {
            setSelectedCommentId(firstUnreadComment.id);
          } else {
            setSelectedCommentId(sortedComments[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des commentaires:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors du chargement des commentaires.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectComment = (commentId: string) => {
    setSelectedCommentId(commentId);
    
    setComments(prevComments => 
      prevComments.map(comment => 
        comment.id === commentId ? { ...comment, isRead: true } : comment
      )
    );
    
    updateCommentReadStatus({ commentId, isRead: true });
  };

  useEffect(() => {
    loadComments();
  }, [selectedProfiles]);

  return {
    comments,
    selectedCommentId,
    isLoading,
    handleSelectComment,
    selectedComment: comments.find(comment => comment.id === selectedCommentId) || null
  };
}
